/**
 * Periscope tutorial — when the install guide is offered, and how it is remembered.
 *
 * Why this exists: placing the periscope badly is the quiet way a store ends up with photos that
 * mix the mirror with whatever is in front of the tablet. The guide (PeriscopeTutorialScreen) is
 * offered once, the first time a tablet logs in, and always available from Ajustes. This module
 * holds the decisions around it, pure and with the storage injected, so they are unit tested
 * without a tablet: is this the first login of THIS device, is the tutorial still pending, does
 * a given way of closing it settle it.
 *
 * First login of a device: `device.last_login` cannot tell (the backend writes it at
 * authentication, before the tablet can read it), so it is detected locally — there is no
 * `bronze_last_login` record (src/loginHelp.js) for this deviceId yet. A tablet that already
 * ran Tracker keeps its record and is never offered the tutorial, nothing written for it.
 *
 * Persistence: one flag per device in localStorage, `pending` | `done`. `pending` is written only
 * at a first login, so closing the welcome without choosing brings it back at the next login;
 * finishing the tutorial or "Ya está colocado" settles it as `done`. Every storage and telemetry
 * call here is best-effort: a tablet whose storage throws must still reach the camera screen.
 */
import posthog from 'posthog-js';
import { INSTALL_ORIGIN, readInstallOrigin } from './installOrigin';

// The install origin lives in its own import-free module (it has to run before anything else at startup);
// it is re-exported here because it is the tutorial's gate.
export { INSTALL_ORIGIN_KEY, INSTALL_ORIGIN, readInstallOrigin, markInstallOrigin } from './installOrigin';

/** The seven guided steps, in order; the screen shows them as "Paso 1..7 de 7". */
export const PERISCOPE_TUTORIAL_STEPS = Object.freeze(['needs', 'clean', 'place', 'align', 'mark', 'stick', 'check']);

/** What is stored per device. No stored state at all means "this tablet predates the tutorial". */
export const PERISCOPE_TUTORIAL_STATE = Object.freeze({ PENDING: 'pending', DONE: 'done' });

/** How the screen was left. */
export const PERISCOPE_OUTCOME = Object.freeze({
  COMPLETED: 'completed',
  ALREADY_INSTALLED: 'already_installed',
  DISMISSED: 'dismissed',
});

const STATES = Object.values(PERISCOPE_TUTORIAL_STATE);

/** localStorage key of the per-device flag. */
export function periscopeTutorialKey(deviceId) {
  return `bronze:periscopeTutorial:${deviceId}`;
}

/** The stored state of this device: 'pending' | 'done', or null when absent, unknown or unreadable. */
export function readPeriscopeTutorialState(storage, deviceId) {
  if (!storage || !deviceId) return null;
  try {
    const value = storage.getItem(periscopeTutorialKey(deviceId));
    return STATES.includes(value) ? value : null;
  } catch (_) {
    return null;
  }
}

/** Store the state of this device. Returns whether it was written; never throws. */
export function writePeriscopeTutorialState(storage, deviceId, state) {
  if (!storage || !deviceId || !STATES.includes(state)) return false;
  try {
    storage.setItem(periscopeTutorialKey(deviceId), state);
    return true;
  } catch (_) {
    return false;
  }
}

/** First login of this device: no last-login record, or the record belongs to another device. */
export function isFirstLoginOnDevice(lastLogin, deviceId) {
  return !lastLogin || lastLogin.deviceId !== deviceId;
}

/**
 * Decide, at login, whether to open the tutorial. Call it BEFORE the last-login record is
 * refreshed for this login (afterwards every login would look like a repeat).
 *
 * Only a FRESH install is ever offered it (src/installOrigin.js): a tablet that already ran Tracker
 * and merely updated is never offered, and nothing is written for it, whatever its last-login record
 * or per-device flag say. Within a fresh install: no state yet + first login on this device writes
 * `pending`, and `pending` is offered again on every tablet login until it is settled.
 *
 * @param {object}  args
 * @param {Storage} args.storage
 * @param {string}  args.deviceId
 * @param {object|null} args.lastLogin  readLastLogin(storage) as it was before this login
 * @param {boolean} args.isTablet       the automatic offer is for tablets only
 * @param {('fresh'|'legacy'|null)} [args.origin]  defaults to the origin recorded in `storage`
 * @returns {{ offer: boolean, state: ('pending'|'done'|null) }}
 */
export function periscopeTutorialOnLogin({ storage, deviceId, lastLogin, isTablet, origin = readInstallOrigin(storage) } = {}) {
  let state = readPeriscopeTutorialState(storage, deviceId);
  if (origin !== INSTALL_ORIGIN.FRESH) return { offer: false, state };
  if (state === null && deviceId && isFirstLoginOnDevice(lastLogin, deviceId)) {
    if (writePeriscopeTutorialState(storage, deviceId, PERISCOPE_TUTORIAL_STATE.PENDING)) {
      state = PERISCOPE_TUTORIAL_STATE.PENDING;
    }
  }
  return { offer: Boolean(isTablet) && state === PERISCOPE_TUTORIAL_STATE.PENDING, state };
}

/**
 * The state to store when the screen closes. Finishing and "already installed" settle it;
 * closing without choosing leaves it as it was, so the welcome comes back at the next login.
 */
export function stateAfterClose(outcome, currentState) {
  if (outcome === PERISCOPE_OUTCOME.COMPLETED || outcome === PERISCOPE_OUTCOME.ALREADY_INSTALLED) {
    return PERISCOPE_TUTORIAL_STATE.DONE;
  }
  return currentState === undefined ? null : currentState;
}

/** Product event `periscope_tutorial_<event>`. Silent in the published demo; never throws. */
export function trackPeriscope(event, props = {}) {
  try {
    // typeof: the constant only exists in the webpack build (see vue.config.js), not in vitest.
    if (typeof __BRONZE_DEMO__ !== 'undefined' && __BRONZE_DEMO__) return;
    posthog.capture(`periscope_tutorial_${event}`, props);
  } catch (_) { /* never let telemetry throw */ }
}
